import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)');
}

// ---------- DEBUG: which Supabase project are we using? ----------
const anonFirst12 = (supabaseAnonKey || '').slice(0, 12);
const projectRef = (() => {
  try {
    const host = new URL(supabaseUrl).host; // e.g. uenyokbwvzplthgskzlu.supabase.co
    return host.split('.')[0];
  } catch {
    return 'unknown';
  }
})();
console.log('[SUPABASE] URL:', supabaseUrl);
console.log('[SUPABASE] Project ref:', projectRef);
console.log('[SUPABASE] ANON first 12:', anonFirst12);
// ---------------------------------------------------------------

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // SECURITY: Only detect sessions on specific callback routes, not globally
    detectSessionInUrl: window.location.pathname.startsWith('/auth/callback') ||
                        window.location.pathname.startsWith('/verify-email'),
    // Explicitly use browser localStorage
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  global: {
    headers: {
      'X-Client-Info': 'companion-finder-web',
    },
    // Add a timeout + log for fetch calls
    fetch: (...args) => {
      const [resource, config] = args as [RequestInfo, RequestInit | undefined];
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('[SUPABASE] Fetch timed out:', resource);
        controller.abort();
      }, 120_000); // 120s

      return fetch(resource, { ...(config || {}), signal: controller.signal })
        .finally(() => clearTimeout(timeoutId));
    },
  },
});

// Log auth state changes for debugging (sanitized)
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Supabase auth event:', event);
  if (session?.expires_at) {
    console.log('Session expires at:', new Date(session.expires_at * 1000).toISOString());
  }

  // SECURITY: Clear URL hash after session is established to prevent token leakage
  if (event === 'SIGNED_IN' && window.location.hash.includes('access_token')) {
    console.warn('[SECURITY] Clearing access token from URL');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
});

// Expose client in the browser console for quick inspection
// eslint-disable-next-line @typescript-eslint/no-explicit-any
if (typeof window !== 'undefined') (window as any).supabase = supabase;
